import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const QuickActionToolbar = ({ 
  actions = [],
  variant = 'default'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const primaryActions = actions?.filter(action => action?.primary);
  const secondaryActions = actions?.filter(action => !action?.primary);

  return (
    <>
      <div className="hidden lg:flex items-center gap-8">
        {actions?.map((action, index) => (
          <Button
            key={index}
            variant={action?.variant || 'default'}
            size="default"
            iconName={action?.icon}
            iconPosition="left"
            iconSize={18}
            onClick={action?.onClick}
            disabled={action?.disabled}
          >
            {action?.label}
          </Button>
        ))}
      </div>
      <div className="lg:hidden">
        {primaryActions?.length > 0 && (
          <div className="flex items-center gap-8 mb-8">
            {primaryActions?.map((action, index) => (
              <Button
                key={index}
                variant={action?.variant || 'default'}
                size="default"
                iconName={action?.icon}
                iconPosition="left"
                iconSize={18}
                onClick={action?.onClick}
                disabled={action?.disabled}
                fullWidth
              >
                {action?.label}
              </Button>
            ))}
          </div>
        )}

        {secondaryActions?.length > 0 && (
          <div className="relative">
            <Button
              variant="outline"
              size="default"
              iconName="MoreHorizontal"
              iconSize={18}
              onClick={() => setIsExpanded(!isExpanded)}
              fullWidth
            >
              More Actions
            </Button>

            {isExpanded && (
              <>
                <div 
                  className="fixed inset-0 z-1050" 
                  onClick={() => setIsExpanded(false)}
                />
                <div className="absolute right-0 top-full mt-8 z-1100 bg-popover rounded-lg shadow-elevation-lg border border-border min-w-[240px] py-8">
                  {secondaryActions?.map((action, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        action?.onClick();
                        setIsExpanded(false);
                      }}
                      disabled={action?.disabled}
                      className={`
                        w-full flex items-center gap-12 px-16 py-12
                        text-sm transition-smooth
                        ${action?.disabled 
                          ? 'text-muted-foreground cursor-not-allowed opacity-50' 
                          : 'text-popover-foreground hover:bg-muted cursor-pointer'
                        }
                      `}
                    >
                      <Icon name={action?.icon} size={18} />
                      {action?.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={`
          lg:hidden fixed bottom-24 right-24 z-200
          w-14 h-14 rounded-full
          bg-primary text-primary-foreground
          shadow-elevation-xl
          flex items-center justify-center
          transition-smooth
          hover:scale-110 active:scale-95
          ${isExpanded ? 'rotate-45' : ''}
        `}
        aria-label="Quick actions"
      >
        <Icon name="Plus" size={24} />
      </button>
      {isExpanded && (
        <div className="lg:hidden fixed bottom-88 right-24 z-200 space-y-12">
          {actions?.slice(0, 4)?.reverse()?.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                action?.onClick();
                setIsExpanded(false);
              }}
              disabled={action?.disabled}
              className={`
                flex items-center gap-12 px-16 py-12
                bg-card text-card-foreground
                rounded-lg shadow-elevation-lg border border-border
                transition-smooth
                hover:scale-105 active:scale-95
                ${action?.disabled ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              style={{
                animation: `slideInRight 250ms cubic-bezier(0.4, 0, 0.2, 1) ${index * 50}ms both`
              }}
            >
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center
                ${action?.variant === 'destructive' ? 'bg-destructive/10 text-destructive' : ''}
                ${action?.variant === 'default' ? 'bg-primary/10 text-primary' : ''}
                ${!action?.variant ? 'bg-muted text-muted-foreground' : ''}
              `}>
                <Icon name={action?.icon} size={18} />
              </div>
              <span className="text-sm font-medium">{action?.label}</span>
            </button>
          ))}
        </div>
      )}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default QuickActionToolbar;